import { Fragment, jsx } from "react/jsx-runtime";
import { isAndroid, isIos, isTouchable, isWeb, useIsomorphicLayoutEffect } from "@tamagui/constants";
import { createStyledContext, useMedia } from "@tamagui/core";
import { withStaticProperties } from "@tamagui/helpers";
import { getPortal } from "@tamagui/native";
import { PortalHost, PortalItem } from "@tamagui/portal";
import { StackZIndexContext } from "@tamagui/z-index-stack";
import React, { createContext, useContext, useId, useMemo } from "react";

function createAdaptChildrenStore() {
	var children = null;
	var listeners = /* @__PURE__ */ new Set();
	return {
		set(c) {
			children = c;
			var _iteratorNormalCompletion = true, _didIteratorError = false, _iteratorError = void 0;
			try {
				for (var _iterator = listeners[Symbol.iterator](), _step; !(_iteratorNormalCompletion = (_step = _iterator.next()).done); _iteratorNormalCompletion = true) {
					var l = _step.value;
					l();
				}
			} catch (err) {
				_didIteratorError = true;
				_iteratorError = err;
			} finally {
				try {
					if (!_iteratorNormalCompletion && _iterator.return != null) _iterator.return();
				} finally {
					if (_didIteratorError) throw _iteratorError;
				}
			}
		},
		get: function() {
			return children;
		},
		subscribe(callback) {
			listeners.add(callback);
			return function() {
				return listeners.delete(callback);
			};
		}
	};
}
var AdaptChildrenStoreContext = /* @__PURE__ */ createContext(null);
var emptySubscribe = function() {
	return function() {};
};
var emptyGet = function() {
	return null;
};
function TeleportAdaptContents() {
	var store = useContext(AdaptChildrenStoreContext);
	var _store_subscribe, _store_get, _store_get1;
	return /* @__PURE__ */ jsx(Fragment, { children: React.useSyncExternalStore((_store_subscribe = store === null || store === void 0 ? void 0 : store.subscribe) !== null && _store_subscribe !== void 0 ? _store_subscribe : emptySubscribe, (_store_get = store === null || store === void 0 ? void 0 : store.get) !== null && _store_get !== void 0 ? _store_get : emptyGet, (_store_get1 = store === null || store === void 0 ? void 0 : store.get) !== null && _store_get1 !== void 0 ? _store_get1 : emptyGet) });
}
var AdaptContext = createStyledContext({
	Contents: null,
	scopeName: "",
	portalName: "",
	platform: null,
	setPlatform: function(x) {},
	when: null,
	setWhen: function() {}
});
var LastAdaptContextScope = /* @__PURE__ */ createContext("");
var ProvideAdaptContext = function(param) {
	var { children, ...context } = param;
	var scope = context.scopeName || "";
	var lastScope = useContext(LastAdaptContextScope);
	return /* @__PURE__ */ jsx(LastAdaptContextScope.Provider, {
		value: lastScope || context.lastScope || "",
		children: /* @__PURE__ */ jsx(AdaptContext.Provider, {
			scope,
			lastScope: lastScope || context.lastScope,
			...context,
			children
		})
	});
};
var useAdaptContext = function(scope) {
	var lastScope = useContext(LastAdaptContextScope);
	var adaptScope = scope !== null && scope !== void 0 ? scope : lastScope;
	return AdaptContext.useStyledContext(adaptScope);
};
var AdaptPortals = /* @__PURE__ */ new Map();
var AdaptParent = function(param) {
	var { children, Contents, scope, portal } = param;
	var id = useId();
	var portalName = `AdaptPortal${scope}${id}`;
	var childrenStoreRef = React.useRef(null);
	if (!childrenStoreRef.current) childrenStoreRef.current = createAdaptChildrenStore();
	var isTeleport = getPortal().state.type === "teleport";
	var FinalContents = useMemo(function() {
		if (Contents) return Contents;
		if (isTeleport) return TeleportAdaptContents;
		if (AdaptPortals.has(portalName)) return AdaptPortals.get(portalName);
		var element = function() {
			return /* @__PURE__ */ jsx(PortalHost, {
				name: portalName,
				forwardProps: typeof portal === "boolean" ? void 0 : portal === null || portal === void 0 ? void 0 : portal.forwardProps
			}, id);
		};
		AdaptPortals.set(portalName, element);
		return element;
	}, [
		portalName,
		Contents,
		isTeleport
	]);
	useIsomorphicLayoutEffect(function() {
		if (!isTeleport) {
			AdaptPortals.set(portalName, FinalContents);
			return function() {
				AdaptPortals.delete(portalName);
			};
		}
	}, [portalName, isTeleport]);
	var [when, setWhen] = React.useState(null);
	var [platform, setPlatform] = React.useState(null);
	return /* @__PURE__ */ jsx(AdaptChildrenStoreContext.Provider, {
		value: childrenStoreRef.current,
		children: /* @__PURE__ */ jsx(LastAdaptContextScope.Provider, {
			value: scope,
			children: /* @__PURE__ */ jsx(ProvideAdaptContext, {
				Contents: FinalContents,
				when,
				platform,
				setPlatform,
				setWhen,
				portalName,
				scopeName: scope,
				children
			})
		})
	});
};
var AdaptContents = function(param) {
	var { scope, ...rest } = param;
	var context = useAdaptContext(scope);
	if (!(context === null || context === void 0 ? void 0 : context.Contents)) throw new Error(process.env.NODE_ENV === "production" ? `tamagui.dev/docs/intro/errors#warning-002` : `You're rendering a Tamagui <Adapt /> component without nesting it inside a parent that is able to adapt.`);
	return /* @__PURE__ */ React.createElement(context.Contents, {
		...rest,
		key: `stable`
	});
};
AdaptContents.shouldForwardSpace = true;
var Adapt = withStaticProperties(function Adapt2(props) {
	var { platform, when, children, scope } = props;
	var context = useAdaptContext(scope);
	var enabled = useAdaptIsActiveGiven(props);
	useIsomorphicLayoutEffect(function() {
		var _context_setWhen, _context_setPlatform;
		context === null || context === void 0 || (_context_setWhen = context.setWhen) == null || _context_setWhen.call(context, when || enabled);
		context === null || context === void 0 || (_context_setPlatform = context.setPlatform) == null || _context_setPlatform.call(context, platform || null);
	}, [
		when,
		platform,
		enabled,
		context.setWhen,
		context.setPlatform
	]);
	useIsomorphicLayoutEffect(function() {
		return function() {
			var _context_setWhen, _context_setPlatform;
			context === null || context === void 0 || (_context_setWhen = context.setWhen) == null || _context_setWhen.call(context, null);
			context === null || context === void 0 || (_context_setPlatform = context.setPlatform) == null || _context_setPlatform.call(context, null);
		};
	}, []);
	var output;
	if (typeof children === "function") {
		var Component = context === null || context === void 0 ? void 0 : context.Contents;
		output = children(Component ? /* @__PURE__ */ jsx(Component, {}) : null);
	} else output = children;
	return /* @__PURE__ */ jsx(StackZIndexContext, { children: !enabled ? null : output });
}, { Contents: AdaptContents });
var AdaptPortalContents = function(props) {
	var isActive = useAdaptIsActive(props.scope);
	var { portalName } = useAdaptContext(props.scope);
	var childrenStore = useContext(AdaptChildrenStoreContext);
	if (!isWeb && getPortal().state.type === "teleport" && childrenStore) return /* @__PURE__ */ jsx(AdaptPortalTeleport, {
		isActive,
		store: childrenStore,
		children: props.children
	});
	return /* @__PURE__ */ jsx(PortalItem, {
		passThrough: !isActive,
		hostName: portalName,
		children: props.children
	});
};
function AdaptPortalTeleport(param) {
	var { isActive, store, children } = param;
	useIsomorphicLayoutEffect(function() {
		if (isActive) store.set(children);
		else store.set(null);
	});
	useIsomorphicLayoutEffect(function() {
		return function() {
			return store.set(null);
		};
	}, [store]);
	return isActive ? null : /* @__PURE__ */ jsx(Fragment, { children });
}
var useAdaptIsActiveGiven = function(param) {
	var { when, platform } = param;
	var media = useMedia();
	if (when == null && platform == null) return false;
	if (when === true) return true;
	var enabled = false;
	if (platform === "touch") enabled = isTouchable;
	else if (platform === "native") enabled = !isWeb;
	else if (platform === "web") enabled = isWeb;
	else if (platform === "ios") enabled = isIos;
	else if (platform === "android") enabled = isAndroid;
	if (platform && enabled == false) return false;
	if (when && typeof when === "string") enabled = media[when];
	return enabled;
};
var useAdaptIsActive = function(scope) {
	return useAdaptIsActiveGiven(useAdaptContext(scope));
};

export { Adapt, AdaptContents, AdaptContext, AdaptParent, AdaptPortalContents, ProvideAdaptContext, useAdaptContext, useAdaptIsActive };